import { pb } from '@/libs/pocketbase'
import { tryCatch } from '@/utils/try-catch'
import type {
	CompanyWorkflow,
	ConnectPhasesInput,
	CreatePhaseInput,
	CreateStepInput,
	CreateTransitionInput,
	CreateWorkflowInput,
	DisconnectPhaseInput,
	Phase,
	Step,
	Team,
	Transition,
	UpdatePhaseInput,
	UpdateStepInput,
	UpdateTransitionInput,
	UpdateWorkflowInput,
	WorkflowPort,
	WorkflowStatus,
} from '../domain/workflow'

// Shape of the raw /api/team-members response: only the fields this adapter
// flattens into the Member view the canvas consumes.
type MemberDirectoryEntry = {
	id: string
	fullName: string
	avatarUrl: string | null
	teamIds: string[]
}

const absoluteAvatar = (url: string | null): string | null =>
	url && url.startsWith('/') ? `${pb.baseURL}${url}` : url

const request = async <T>(path: string, init?: { method?: string; body?: unknown }): Promise<T> => {
	const method = init?.method ?? 'GET'
	const hasBody = init?.body !== undefined
	const result = await tryCatch(
		pb.send<T>(`/api${path}`, {
			method,
			requestKey: null,
			...(hasBody ? { body: JSON.stringify(init?.body), headers: { 'Content-Type': 'application/json' } } : {}),
		}),
	)
	if (!result.isSuccess) throw new Error(result.error.message)
	return result.data
}

type ListResult<T> = { items: T[] } | null

const unwrap = <T>(raw: ListResult<T> | T[]): T[] => (Array.isArray(raw) ? raw : (raw?.items ?? []))

const pbWorkflows: WorkflowPort = {
	listAll: () => request<ListResult<CompanyWorkflow> | CompanyWorkflow[]>('/workflows').then(unwrap),
	getById: id => request<CompanyWorkflow | null>(`/workflows/${id}`),
	listTeams: () => request<Team[]>('/teams'),
	listMembers: teamId =>
		request<MemberDirectoryEntry[]>('/team-members').then(members =>
			members
				.flatMap(member =>
					member.teamIds.map(id => ({
						id: member.id,
						name: member.fullName,
						avatarUrl: absoluteAvatar(member.avatarUrl),
						teamId: id,
					})),
				)
				.filter(member => member.teamId === teamId),
		),
	listAllMembers: () =>
		request<MemberDirectoryEntry[]>('/team-members').then(members =>
			members.map(member => ({
				id: member.id,
				name: member.fullName,
				avatarUrl: absoluteAvatar(member.avatarUrl),
				teamId: member.teamIds[0] ?? '',
			})),
		),
	createWorkflow: (input: CreateWorkflowInput) => request<CompanyWorkflow>('/workflows', { method: 'POST', body: input }),
	updateWorkflow: (input: UpdateWorkflowInput) =>
		request<CompanyWorkflow>(`/workflows/${input.id}`, { method: 'PATCH', body: input }),
	setWorkflowStatus: (id: string, status: WorkflowStatus) =>
		request<CompanyWorkflow>(`/workflows/${id}/status`, { method: 'POST', body: { status } }),
	createPhase: (input: CreatePhaseInput) =>
		request<Phase>(`/workflows/${input.companyWorkflowId}/phases`, { method: 'POST', body: input }),
	updatePhase: (input: UpdatePhaseInput) => request<Phase>(`/phases/${input.id}`, { method: 'PATCH', body: input }),
	connectPhases: (input: ConnectPhasesInput) =>
		request<CompanyWorkflow>(`/workflows/${input.workflowId}/phases/connect`, { method: 'POST', body: input }),
	disconnectPhase: (input: DisconnectPhaseInput) =>
		request<CompanyWorkflow>(`/workflows/${input.workflowId}/phases/disconnect`, { method: 'POST', body: input }),
	deletePhase: (id: string) => request<void>(`/phases/${id}`, { method: 'DELETE' }),
	createStep: (input: CreateStepInput) =>
		request<Step>(`/team-workflows/${input.teamWorkflowId}/steps`, { method: 'POST', body: input }),
	updateStep: (input: UpdateStepInput) => request<Step>(`/steps/${input.id}`, { method: 'PATCH', body: input }),
	deleteStep: (id: string) => request<void>(`/steps/${id}`, { method: 'DELETE' }),
	createTransition: (input: CreateTransitionInput) =>
		request<Transition>(`/steps/${input.fromStepId}/transitions`, { method: 'POST', body: input }),
	updateTransition: (input: UpdateTransitionInput) =>
		request<CompanyWorkflow>(`/transitions/${input.id}`, { method: 'PATCH', body: input }),
	deleteTransition: (id: string) => request<void>(`/transitions/${id}`, { method: 'DELETE' }),
}

export { pbWorkflows }
